import React from "react";
import { Alert, Spin, Table } from "antd";
import { ColumnType, TableProps } from "antd/lib/table";
import { compact, filter, groupBy, isEmpty, isNil, map } from "lodash";
import { EComModal } from "components/EComModal";
import { EMPTY } from "constants/common";
import { AiccAssetUploadIssue, AiccAssetUploadPreview } from "types/model/aiccLearningAssets";
import styles from "./LearningAssets.module.scss";

interface Props<T> {
  visible: boolean;
  fileName?: string;
  preview?: AiccAssetUploadPreview<T>;
  rows: T[];
  columns: ColumnType<T>[];
  trailingColumns?: ColumnType<T>[];
  rowKey: TableProps<T>["rowKey"];
  summary: string;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  children?: React.ReactNode;
}

const renderIssues = (issues: AiccAssetUploadIssue[]) =>
  map(issues, ({ type, message }, index) => <div key={`${type}-${index}`}>{message}</div>);

function UploadPreviewModal<T extends object>({
  visible,
  fileName,
  preview,
  rows,
  columns,
  trailingColumns,
  rowKey,
  summary,
  isSaving,
  onCancel,
  onConfirm,
  children,
}: Props<T>) {
  const errors = preview?.errors ?? [];

  const fileErrors = filter(errors, ({ row }) => isNil(row));
  const errorsByRow = groupBy(
    filter(errors, ({ row }) => !isNil(row)),
    "row"
  );

  const getRowErrors = (index: number) => errorsByRow[index] ?? [];

  const errorColumn: ColumnType<T> | false = !isEmpty(errorsByRow) && {
    title: "Error",
    key: "issue",
    width: 280,
    render: (_: unknown, __: T, index: number) => {
      const rowErrors = getRowErrors(index);
      if (isEmpty(rowErrors)) {
        return EMPTY;
      }
      return map(rowErrors, ({ type, message }, key) => (
        <div key={`error-${type}-${key}`} className={styles.uploadErrorText}>
          {message}
        </div>
      ));
    },
  };

  return (
    <EComModal
      visible={visible}
      width={1000}
      title="Upload Preview"
      okText="Confirm"
      maskClosable={false}
      okButtonProps={{ disabled: !preview?.isValid || isEmpty(rows) }}
      onOk={onConfirm}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      <Spin spinning={isSaving} size="large">
        {fileName && <div className={styles.fileName}>{fileName}</div>}
        {!isEmpty(fileErrors) && (
          <Alert
            className={styles.uploadAlert}
            type="error"
            showIcon
            message="This file cannot be uploaded."
            description={renderIssues(fileErrors)}
          />
        )}
        {preview?.isValid && <Alert className={styles.uploadAlert} type="success" showIcon message={summary} />}
        <Table<T>
          className={styles.previewTable}
          size="small"
          columns={compact([...columns, errorColumn, ...(trailingColumns ?? [])])}
          dataSource={rows}
          rowKey={rowKey}
          rowClassName={(_, index) => (isEmpty(getRowErrors(index)) ? "" : styles.errorRow)}
          pagination={false}
          scroll={{ x: "100%", y: 320 }}
        />
      </Spin>
      {children}
    </EComModal>
  );
}

export default UploadPreviewModal;
